import { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import SignatureCanvas from 'react-signature-canvas';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { Plus, X, RefreshCw, Download, CheckCircle, FileText, PenTool } from 'lucide-react';

const SignatureModal = ({ isOpen, onClose, onSave, documentInfo }) => {
  const sigCanvas = useRef(null);
  const [signerName, setSignerName] = useState('');
  const [signerEmail, setSignerEmail] = useState('');

  if (!isOpen) return null;

  const clear = () => {
    sigCanvas.current?.clear();
  };

  const handleSave = () => {
    if (!signerName || !signerEmail) {
      toast.error('Please enter name and email');
      return;
    }
    if (sigCanvas.current?.isEmpty()) {
      toast.error('Please provide a signature');
      return;
    }

    const signatureData = sigCanvas.current.toDataURL('image/png');
    onSave({
      document_id: documentInfo?.id || 'manual',
      document_type: documentInfo?.type || 'general',
      signer_name: signerName,
      signer_email: signerEmail,
      signature_data: signatureData,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-xl">
        <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-[#0A1628]">Add Signature</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Full Name *</Label>
              <Input
                value={signerName}
                onChange={(e) => setSignerName(e.target.value)}
                placeholder="John Doe"
                className="rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input
                type="email"
                value={signerEmail}
                onChange={(e) => setSignerEmail(e.target.value)}
                placeholder="john@example.com"
                className="rounded-xl"
              />
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Signature</Label>
              <Button type="button" variant="outline" size="sm" onClick={clear} className="rounded-lg">
                <RefreshCw className="w-4 h-4 mr-1" /> Clear
              </Button>
            </div>
            <div className="border-2 border-dashed border-gray-300 rounded-xl bg-gray-50">
              <SignatureCanvas
                ref={sigCanvas}
                canvasProps={{
                  className: 'w-full h-48 rounded-xl',
                  style: { width: '100%', height: '192px' }
                }}
                backgroundColor="rgb(249, 250, 251)"
                penColor="#014DB7"
              />
            </div>
            <p className="text-xs text-gray-500 text-center">Draw your signature above</p>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-xl">
              Cancel
            </Button>
            <Button onClick={handleSave} className="bg-[#014DB7] hover:bg-[#003A8C] text-white rounded-xl">
              <CheckCircle className="w-4 h-4 mr-2" /> Save Signature
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const SignatureCard = ({ signature }) => (
  <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
    <div className="flex items-start justify-between mb-4">
      <div>
        <h4 className="font-semibold text-[#0A1628]">{signature.signer_name}</h4>
        <p className="text-sm text-gray-500">{signature.signer_email}</p>
      </div>
      <CheckCircle className="w-5 h-5 text-[#84CC16]" />
    </div>
    
    <div className="bg-gray-50 rounded-lg p-3 mb-4">
      <img 
        src={signature.signature_data} 
        alt="Signature" 
        className="max-h-20 mx-auto"
      />
    </div>

    <div className="flex items-center justify-between text-sm">
      <span className="text-gray-500 flex items-center gap-1">
        <FileText className="w-4 h-4" />
        {signature.document_type}
      </span>
      <span className="text-gray-400 text-xs">
        {new Date(signature.signed_at).toLocaleString()}
      </span>
    </div>
  </div>
);

export const SignaturesPage = () => {
  const { api } = useAuth();
  const [signatures, setSignatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);

  const fetchSignatures = async () => {
    try {
      const response = await api.get('/api/signatures');
      setSignatures(response.data);
    } catch (error) {
      console.error('Failed to fetch signatures:', error);
      toast.error('Failed to load signatures');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSignatures();
  }, []);

  const handleSaveSignature = async (data) => {
    try {
      await api.post('/api/signatures', data);
      toast.success('Signature saved');
      setModalOpen(false);
      fetchSignatures();
    } catch (error) {
      toast.error('Failed to save signature');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-[#014DB7] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#0A1628]">E-Signatures</h1>
          <p className="text-gray-500">Manage digital signatures</p>
        </div>
        <Button onClick={() => setModalOpen(true)} className="bg-[#014DB7] hover:bg-[#003A8C] text-white rounded-xl">
          <PenTool className="w-4 h-4 mr-2" /> New Signature
        </Button>
      </div>

      {/* Info Card */}
      <div className="bg-gradient-to-r from-[#014DB7] to-[#0066FF] rounded-2xl p-6 text-white">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
            <PenTool className="w-6 h-6" />
          </div>
          <div>
            <h3 className="font-semibold text-lg mb-1">Digital Signature System</h3>
            <p className="text-white/80 text-sm">
              Capture legally binding electronic signatures for contracts, agreements, and documents.
              All signatures are timestamped and stored securely.
            </p>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl p-5 border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#014DB7]/10 rounded-lg flex items-center justify-center">
              <PenTool className="w-5 h-5 text-[#014DB7]" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Signatures</p>
              <p className="text-xl font-bold text-[#0A1628]">{signatures.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-5 border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#84CC16]/10 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-[#84CC16]" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Verified</p>
              <p className="text-xl font-bold text-[#84CC16]">{signatures.filter(s => s.verified).length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-5 border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <FileText className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Documents</p>
              <p className="text-xl font-bold text-purple-600">
                {new Set(signatures.map(s => s.document_id)).size}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Signatures Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {signatures.map(signature => (
          <SignatureCard key={signature.id} signature={signature} />
        ))}
        {signatures.length === 0 && (
          <div className="col-span-full text-center py-12">
            <PenTool className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-600 mb-2">No signatures yet</h3>
            <p className="text-gray-500 mb-4">Start collecting e-signatures for your documents</p>
            <Button onClick={() => setModalOpen(true)} className="bg-[#014DB7] text-white rounded-xl">
              <Plus className="w-4 h-4 mr-2" /> Create First Signature
            </Button>
          </div>
        )}
      </div>

      <SignatureModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleSaveSignature}
      />
    </div>
  );
};

export default SignaturesPage;
